import { Form, Formik, useField, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import type { Client } from "@/features/clients/types";
import { importBankAccount, listImportableBankAccounts } from "@/features/forms/api";
import {
  FILE_AS_OPTIONS,
  type FileAs,
  type Form433DData,
  type FormSubmission,
  type ImportableBankAccount,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox, FormikSelect } from "./formik-controls";

const PAYMENT_DAYS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS",
  "KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY",
  "NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV",
  "WI","WY","DC",
].map((s) => ({ value: s, label: s }));

const money = Yup.string().test(
  "non-negative",
  "Must be zero or greater",
  (v) => v == null || v === "" || (!Number.isNaN(Number(v)) && Number(v) >= 0),
);

const schema = Yup.object({
  file_as: Yup.string().oneOf(["T", "S", "J"]).required(),
  tax_forms: Yup.string().max(40, "Too long"),
  tax_due: money,
  tax_payment: money,
  monthly_payment: money,
  increase_amt_1: money,
  increase_amt_2: money,
  routing_number: Yup.string()
    .matches(/^\d*$/, "Digits only")
    .test("len", "Must be 9 digits", (v) => !v || v.replace(/\D/g, "").length === 9),
  account_number: Yup.string()
    .matches(/^\d*$/, "Digits only")
    .max(17, "At most 17 digits"),
  payment_day: Yup.string(),
});

function toInitialValues(data: Record<string, unknown>): Form433DData {
  const d = data as Partial<Form433DData>;
  return {
    file_as: d.file_as ?? "T",
    tax_forms: d.tax_forms ?? "",
    tax_periods: d.tax_periods ?? "",
    tax_due: d.tax_due ?? "0",
    tax_payment: d.tax_payment ?? "0",
    first_pmt_date: d.first_pmt_date ?? "",
    monthly_payment: d.monthly_payment ?? "0",
    payment_day: d.payment_day ?? "",
    routing_number: d.routing_number ?? "",
    account_number: d.account_number ?? "",
    no_electronic: d.no_electronic ?? false,
    increase_amt_1: d.increase_amt_1 ?? "0",
    increase_date_1: d.increase_date_1 ?? "",
    increase_amt_2: d.increase_amt_2 ?? "0",
    increase_date_2: d.increase_date_2 ?? "",
    tp_employer: d.tp_employer ?? "",
    tp_address_1: d.tp_address_1 ?? "",
    tp_city: d.tp_city ?? "",
    tp_state: d.tp_state ?? "",
    tp_zip_code: d.tp_zip_code ?? "",
    bank_name: d.bank_name ?? "",
    bank_street: d.bank_street ?? "",
    bank_csz: d.bank_csz ?? "",
  };
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

function LabeledCell({
  name,
  label,
  ...props
}: { name: string; label: string } & React.ComponentProps<typeof FormikCell>) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={name} className="text-sm font-normal">
        {label}
      </Label>
      <FormikCell name={name} {...props} />
    </div>
  );
}

function FileAsField({ married }: { married: boolean }) {
  const [field, , helpers] = useField<FileAs>("file_as");
  // The filer selector only matters for a married client; otherwise it's the
  // taxpayer (legacy shows it only when marital_status = Married).
  if (!married) return <p className="text-muted-foreground text-sm">Taxpayer</p>;
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as FileAs)}
    >
      {FILE_AS_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`fa-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`fa-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

function TaxPeriodsField() {
  const [field] = useField("tax_periods");
  return (
    <div className="grid gap-1.5">
      <Label htmlFor="tax_periods" className="text-sm font-normal">
        Tax periods
      </Label>
      <Textarea id="tax_periods" rows={3} {...field} />
    </div>
  );
}

/** 433-D only: pick a stored personal (433-A) account and import its number. */
function BankImportField({ submissionId }: { submissionId: string }) {
  const { setFieldValue } = useFormikContext<Form433DData>();
  const [accounts, setAccounts] = useState<ImportableBankAccount[]>([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    listImportableBankAccounts(submissionId)
      .then((a) => active && setAccounts(a))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [submissionId]);

  if (accounts.length === 0) return null;

  async function handleImport() {
    if (!selected) return;
    setBusy(true);
    try {
      const { accountNumber } = await importBankAccount(submissionId, selected);
      await setFieldValue("account_number", accountNumber);
      // Routing isn't stored on the worksheet account — clear it for re-entry.
      await setFieldValue("routing_number", "");
      toast.success("Imported account number from 433-A");
    } catch {
      toast.error("Couldn't import that account.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="grid gap-1.5">
        <Label className="text-sm font-normal">Import from 433-A</Label>
        <select
          aria-label="Personal account to import"
          className="border-input bg-background h-9 rounded-md border px-3 text-sm"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">Select an account…</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.institution} {a.masked}
            </option>
          ))}
        </select>
      </div>
      <Button type="button" variant="outline" size="sm" disabled={!selected || busy} onClick={handleImport}>
        {busy ? "Importing…" : "Import"}
      </Button>
    </div>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form 433-D (Installment Agreement) and its Spanish variant. Payment
 * terms are entered here (not derived); identity + tax periods prefill from the
 * client + transcripts. 433-D can import a bank number from a stored 433-A
 * account; 433-D(SP) instead exposes editable employer + bank-institution
 * fields (and no import).
 */
export function Form433DEditor({ client, submission, onSubmissionChange }: Props) {
  const spanish = submission.formNumber.includes("(SP)");
  const married = client.maritalStatus === "MARRIED";
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-4 p-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-foreground font-semibold">File as</span>
            <FileAsField married={married} />
          </div>
          <div className="text-muted-foreground text-sm">
            <p className="text-foreground font-medium">{client.name}</p>
            {address && <p>{address}</p>}
          </div>
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Tax liability</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="tax_forms" label="Tax form(s)" placeholder="1040" />
            <LabeledCell name="tax_due" label="Total amount owed ($)" inputMode="decimal" />
          </div>
          <TaxPeriodsField />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Bank account (Direct Debit)</SectionHeading>
          {!spanish && <BankImportField submissionId={submission.id} />}
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="routing_number" label="Routing number (9 digits)" inputMode="numeric" maxLength={9} />
            <LabeledCell name="account_number" label="Account number (≤ 17 digits)" inputMode="numeric" maxLength={17} />
          </div>
          {spanish && (
            <div className="grid gap-4 sm:grid-cols-3">
              <LabeledCell name="bank_name" label="Bank name" />
              <LabeledCell name="bank_street" label="Bank street" />
              <LabeledCell name="bank_csz" label="Bank city, state ZIP" />
            </div>
          )}
          <FormikCheckbox
            name="no_electronic"
            label="Unable to make electronic (direct debit) payments"
          />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Payment terms</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="tax_payment" label="Initial payment ($)" inputMode="decimal" />
            <LabeledCell name="first_pmt_date" label="Initial payment date" type="date" />
            <LabeledCell name="monthly_payment" label="Monthly payment ($)" inputMode="decimal" />
            <div className="grid gap-1.5">
              <Label className="text-sm font-normal">Payment day of month</Label>
              <FormikSelect name="payment_day" options={PAYMENT_DAYS} placeholder="Day" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <LabeledCell name="increase_amt_1" label="Scheduled increase #1 ($)" inputMode="decimal" />
            <LabeledCell name="increase_date_1" label="Increase #1 date" type="date" />
            <LabeledCell name="increase_amt_2" label="Scheduled increase #2 ($)" inputMode="decimal" />
            <LabeledCell name="increase_date_2" label="Increase #2 date" type="date" />
          </div>
        </Card>

        {spanish && (
          <Card className="gap-4 p-6">
            <SectionHeading>Employer</SectionHeading>
            <div className="grid gap-4 sm:grid-cols-2">
              <LabeledCell name="tp_employer" label="Employer name" />
              <LabeledCell name="tp_address_1" label="Employer street" />
              <LabeledCell name="tp_city" label="City" />
              <div className="grid gap-1.5">
                <Label className="text-sm font-normal">State</Label>
                <FormikSelect name="tp_state" options={US_STATES} placeholder="State" />
              </div>
              <LabeledCell name="tp_zip_code" label="ZIP" />
            </div>
          </Card>
        )}

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
